<template>
  <div tabindex="0" :class="cls" v-bind="eventHandlers">
    <span :class="`${prefixCls}-title`">
      <slot />
    </span>
    <template v-if="$attrs.type === 'graph'">
      <div :class="`${prefixCls}-card-bg`">
        <svg
          v-if="Number($attrs.tabIndex) === 0 && active"
          :class="`${prefixCls}-bg-icon`"
          width="184px"
          height="56px"
          viewBox="0 0 152 48"
          version="1.1"
          preserveAspectRatio="none meet"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <g
            id="background-tabs"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
          >
            <g id="background-items" fill="#FFFFFF">
              <path
                id="矩形"
                d="M8,0 L114.396582,0 C117.760633,2.93474744e-15 120.765288,2.104542 121.914929,5.2660555 L130.363829,28.5005298 C133.887865,38.1916279 141.995944,45.498986 152,48 L152,48 L152,48 L0,48 L0,8 C-5.41083001e-16,3.581722 3.581722,-1.85291076e-15 8,0 Z"
              />
            </g>
          </g>
        </svg>
        <svg
          v-if="Number($attrs.tabIndex) !== 0 && active"
          :class="`${prefixCls}-bg-icon`"
          width="216px"
          height="56px"
          viewBox="0 0 184 48"
          version="1.1"
          preserveAspectRatio="none meet"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <g
            id="background-tabs"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
          >
            <g id="background-items" fill="#FFFFFF">
              <path
                id="矩形"
                d="M37.6034181,0 L146.396582,0 C149.760633,8.26381796e-15 152.765288,2.104542 153.914929,5.2660555 L162.363829,28.5005298 C165.887865,38.1916279 173.995944,45.498986 184,48 L184,48 L184,48 L0,48 C10.0040561,45.498986 18.1121353,38.1916279 21.636171,28.5005298 L30.0850707,5.2660555 C31.234712,2.104542 34.2393666,-2.04656902e-15 37.6034181,0 Z"
              />
            </g>
          </g>
        </svg>
      </div>
    </template>

    <icon-hover
      v-if="editable && tab.closable"
      :class="`${prefixCls}-close-btn`"
      @click.stop="handleDelete"
    >
      <icon-close />
    </icon-hover>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, inject } from 'vue';
import { getPrefixCls } from '../_utils/global-config';
import IconHover from '../_components/icon-hover.vue';
import IconClose from '../icon/icon-close';
import type { TabData } from './interface';
import { TabsContext, tabsInjectionKey } from './context';

export default defineComponent({
  name: 'TabsTab',
  components: {
    IconHover,
    IconClose,
  },
  props: {
    tab: {
      type: Object as PropType<TabData>,
      required: true,
    },
    active: Boolean,
    editable: Boolean,
  },
  emits: ['click', 'delete'],
  setup(props, { emit }) {
    const prefixCls = getPrefixCls('tabs-tab');
    const tabsCtx = inject<Partial<TabsContext>>(tabsInjectionKey, {});
    const handleClick = (e: Event) => {
      if (!props.tab.disabled) {
        emit('click', props.tab.key, e);
      }
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') {
        handleClick(ev);
      }
    };

    const eventHandlers = computed(() => {
      return Object.assign(
        tabsCtx.trigger === 'click'
          ? { onClick: handleClick }
          : { onMouseover: handleClick },
        { onKeydown: onKeyDown }
      );
    });

    const handleDelete = (e: Event) => {
      if (!props.tab.disabled) {
        emit('delete', props.tab.key, e);
      }
    };

    const cls = computed(() => [
      prefixCls,
      {
        [`${prefixCls}-active`]: props.active,
        [`${prefixCls}-closable`]: props.editable && props.tab.closable,
        [`${prefixCls}-disabled`]: props.tab.disabled,
      },
    ]);

    return {
      prefixCls,
      cls,
      eventHandlers,
      handleDelete,
    };
  },
});
</script>
